// @flow

import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
  useLayoutEffect,
} from "react";
import * as R from "ramda";
import { toast } from "react-toastify";

import {
  Text,
  Flex,
  Modal,
  ModalHeader,
  ModalContent,
  Heading,
} from "@unifize/sarah";

import Header from "./Header";
import Body from "./Body";
import Filters from "./Filters";
import SingleSelect from "src/components/Unifize/Select/SingleSelect";
import Icon from "src/icons";
import { useLanguage } from "src/contexts/LanguageContext";

import { getTranslations, getOrgLanguages } from "src/api/translation";

import * as colors from "src/styles/constants/colors";
import SearchInput from "src/components/Unifize/Input/SearchInput";
import SpinnerLoader from "src/components/Loader/SpinnerLoader";

import {
  SpinnerContainer,
  HeaderRow,
  Container,
  HeaderContainer,
  SupportedLanguagesContainer,
  MenuBar,
  Footer,
  PageButton,
  PageIndicatorContainer,
  PageInput,
  ContentContainer,
  searchInput,
  TableBody,
} from "./styles";
import type { TranslationQueryParams } from "src/types";
import "react-virtualized/styles.css";

const Translations = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(true);

  const containerRef = useRef();
  const [totalPages, setTotalPages] = useState(1);
  const [supportedLanguages, setSupportedLanguages] = useState({});
  const debounceTimeoutRef = useRef(null);
  const pageDebounceTimeoutRef = useRef(null);

  const abortControllerRef = useRef(null);

  const [containerWidth, setContainerWidth] = useState(
    containerRef.current?.clientWidth,
  );

  const {
    language,
    setLanguage,
    setTranslations,
    searchLanguage,
    setSearchLanguage,
  } = useLanguage();

  const syncHeaderWidth = () => {
    // $FlowFixMe
    setContainerWidth(`${parseInt(containerRef.current.clientWidth)}px`);
  };

  const primaryOrgLanguage = useMemo(() => {
    // $FlowFixMe
    return Object.values(supportedLanguages).find((lang) => lang.isPrimary)
      ?.langAbbr;
  }, [supportedLanguages]);

  useEffect(() => {
    if (primaryOrgLanguage) {
      setSearchLanguage(primaryOrgLanguage);
    }
  }, [primaryOrgLanguage]);

  useLayoutEffect(() => {
    if (!containerRef || !containerRef.current) return;

    syncHeaderWidth();

    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(syncHeaderWidth);
    });

    // $FlowFixMe
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [Object.values(supportedLanguages).length]);

  const primaryLanguageName = "English";

  const headers = [
    `${primaryLanguageName} (Primary Language)`,
    `${language !== null ? supportedLanguages[language]?.nativeName : ""} (Translated Language)`,
    "Path",
  ];

  const [queryParams, setQueryParams] = useState({
    lang: language,
    page: 1,
    pageSize: 23,
    search: "",
    searcht: "",
    area: undefined,
    areaId: undefined,
    entity: undefined,
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getOrgLanguages()
      .then((response) => {
        const formattedLanguageData = {};
        if (response?.length) {
          response.forEach((lang) => {
            formattedLanguageData[lang.langAbbr] = lang;
          });

          setSupportedLanguages(formattedLanguageData);

          if (Object.keys(formattedLanguageData).length > 1) {
            for (let lang in formattedLanguageData) {
              if (!formattedLanguageData[lang].isPrimary) {
                setLanguage(lang);
                setQueryParams((prev) => ({
                  ...prev,
                  lang,
                }));
                break;
              }
            }
          }
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to load supported languages");
      })
      .finally(() => {
        // Hide the loader if API request is complete
        setShowLoader(false);
      });
  }, []);

  const fetchTranslations = useCallback(
    async (params?: TranslationQueryParams) => {
      if (isLoading || !queryParams.lang) return;

      // Cancel any ongoing request
      // $FlowFixMe - flow doesn't support optional chaining
      abortControllerRef.current?.abort();

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      try {
        const response = await getTranslations(
          params ?? queryParams,
          abortControllerRef.current?.signal,
        );
        setTranslations(response.translations);
        setQueryParams((prev) => ({
          ...prev,
          page: parseInt(response.pagination.currentPage),
          pageSize: parseInt(response.pagination.pageSize),
        }));
        setTotalPages(response.pagination.totalPages);
      } catch (err) {
        // Only log errors that aren't from cancellation
        if (err.name !== "AbortError") {
          console.error(err);
        }
      }
    },
    [JSON.stringify(queryParams), language],
  );

  useEffect(() => {
    setIsLoading(true);
    fetchTranslations().then(() => {
      setIsLoading(false);
    });
  }, [JSON.stringify(queryParams)]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (pageDebounceTimeoutRef.current) {
        clearTimeout(pageDebounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSearch = useCallback(
    (query) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (searchLanguage && searchLanguage !== primaryOrgLanguage) {
          setQueryParams((prev) => ({
            ...prev,
            page: 1,
            search: "",
            searcht: query,
          }));
        } else {
          setQueryParams((prev) => ({
            ...prev,
            page: 1,
            search: query,
            searcht: "",
          }));
        }
      }, 300);
    },
    [searchLanguage, primaryOrgLanguage],
  );

  const changePage = useCallback((page: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  const nextPage = async () => {
    handlePageInputChange(queryParams.page + 1);
  };

  const previousPage = async () => {
    handlePageInputChange(queryParams.page - 1);
  };

  const handleLanguageSwitch = (e) => {
    setLanguage(e);

    // Set the search language to second language if the user was
    // searching in second language
    if (primaryOrgLanguage && primaryOrgLanguage !== searchLanguage) {
      setSearchLanguage(e);
    }

    setQueryParams((prev) => ({
      ...prev,
      lang: e,
    }));
  };

  const handleSearchLanguageSwitch = (e) => {
    setSearchLanguage(e);

    if (queryParams.search && e !== primaryOrgLanguage) {
      // Clear the regular search and set value for translated search
      setQueryParams((prev) => ({
        ...prev,
        page: 1,
        search: "",
        searcht: queryParams.search,
      }));
    } else if (queryParams.searcht && e === primaryOrgLanguage) {
      setQueryParams((prev) => ({
        ...prev,
        page: 1,
        searcht: "",
        search: queryParams.searcht,
      }));
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const handlePageInputChange = useCallback(
    (value) => {
      if (pageDebounceTimeoutRef.current) {
        clearTimeout(pageDebounceTimeoutRef.current);
      }

      pageDebounceTimeoutRef.current = setTimeout(() => {
        const page = value ? Number(value) : 1;
        if (page >= 1 && page <= totalPages) {
          changePage(page);
        }
      }, 300);
    },
    [totalPages, changePage],
  );

  const searchableLanguages = useMemo(() => {
    if (!language)
      return [
        {
          langAbbr: "en",
          englishName: "English",
          nativeName: "English",
          isPrimary: true,
        },
      ];

    return [
      {
        langAbbr: "en",
        englishName: "English",
        nativeName: "English",
        isPrimary: true,
      },
      {
        ...supportedLanguages[language],
      },
    ];
  }, [supportedLanguages, language]);

  return (
    <Container>
      <Modal
        height="auto"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <ModalHeader>Supported Languages</ModalHeader>
        <ModalContent>
          <ContentContainer>
            Languages supported by your org are:
            <ul>
              {Object.keys(supportedLanguages).map((lang) => (
                <li key={lang}>{supportedLanguages[lang].nativeName}</li>
              ))}
            </ul>
            Contact Unifize to get support for more languages.
          </ContentContainer>
        </ModalContent>
      </Modal>
      <HeaderContainer>
        <Heading variant="h5">Translations</Heading>
        <SupportedLanguagesContainer onClick={openModal}>
          <Icon
            type="info"
            size={15}
            viewBoxSize={19}
            fillColor={colors.blue700}
          />
          <span>Supported languages</span>
        </SupportedLanguagesContainer>
      </HeaderContainer>
      {Object.values(supportedLanguages).length < 2 ? (
        R.isEmpty(supportedLanguages) && showLoader ? (
          <SpinnerContainer>
            <SpinnerLoader />
            <Text>Loading translations</Text>
          </SpinnerContainer>
        ) : (
          <div
            style={{
              padding: "1.5rem",
            }}
          >
            <Text>
              Language support is not available for your org. Contact Unifize to
              get support for more languages
            </Text>
          </div>
        )
      ) : (
        <>
          <MenuBar>
            <Flex
              direction="horizontal"
              width="100%"
              style={{
                maxWidth: "633px",
              }}
            >
              <SingleSelect
                selected={searchLanguage ?? ""}
                placeholder=""
                data={searchableLanguages}
                keys={["langAbbr", "nativeName"]}
                onChange={handleSearchLanguageSwitch}
                width="154px"
              />
              <SearchInput
                placeholder="Search"
                onInput={handleSearch}
                style={searchInput}
              />
            </Flex>
            <div>
              <SingleSelect
                selected={language ?? ""}
                placeholder=""
                data={Object.values(supportedLanguages).filter(
                  // $FlowFixMe
                  (lang) => !lang.isPrimary,
                )}
                keys={["langAbbr", "nativeName"]}
                onChange={handleLanguageSwitch}
                toggleButtonColor={colors.blue700}
                leftIcon={
                  <Icon
                    type="translation"
                    style={{ width: "20px", height: "20px" }}
                    color={colors.blue700}
                  />
                }
              />
            </div>
          </MenuBar>
          {/* $FlowFixMe - doesn't use queryParams.lang */}
          <Filters queryParams={queryParams} setQueryParams={setQueryParams} />
          <TableBody ref={containerRef}>
            <HeaderRow style={{ width: containerWidth }}>
              {headers.map((header, index) => (
                <Header key={index} label={header} />
              ))}
            </HeaderRow>
            <div style={{ height: "24px", width: "100%" }}></div>
            <Body isLoading={isLoading} />
          </TableBody>
          <Footer>
            <PageButton
              className="border rounded p-2"
              onClick={previousPage}
              disabled={queryParams.page <= 1}
            >
              {"<"}
            </PageButton>
            <PageButton
              className="border rounded p-2"
              onClick={nextPage}
              disabled={queryParams.page >= totalPages}
            >
              {">"}
            </PageButton>
            <PageIndicatorContainer>
              <div>Page</div>
              <strong>
                {queryParams.page} of {totalPages}
              </strong>
            </PageIndicatorContainer>
            <PageIndicatorContainer>
              | Go to page:
              <PageInput
                type="number"
                defaultValue={1}
                min={1}
                max={totalPages}
                onChange={(e) => handlePageInputChange(e.target.value)}
                className="border p-2 rounded w-16"
              />
            </PageIndicatorContainer>
          </Footer>
        </>
      )}
    </Container>
  );
};

export default Translations;
